import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { useUser } from '../context/UserContext';
import type { Song } from '../services/SongService';
import { Music } from 'lucide-react';

export const AuditionSongs: React.FC = () => {
  const { songService } = useData();
  const { user } = useUser();
  const navigate = useNavigate();

  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const auditionSongs = await songService.getAuditionSongs();
        setSongs(auditionSongs);
      } catch (error) {
        console.error('Error loading songs:', error);
        setError('Failed to load songs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [songService]);

  const playSong = (song: Song) => {
    navigate(`/kod-studio/${song.id}`);
  };
  
  const recordSong = (e: React.MouseEvent, song: Song) => {
    e.stopPropagation();
    navigate(`/record/${song.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-kod-purple-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-kod-purple-900 flex items-center justify-center p-4">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 max-w-md w-full text-center">
          <p className="text-red-400">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 rounded-lg text-white"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kod-purple-900 flex items-center justify-center p-4">
      <div className="max-w-3xl w-full bg-kod-purple-800 rounded-lg shadow-lg p-8 border border-kod-purple-600/40">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Choose Your Audition Song</h1>
          <p className="text-white/70">Select a song to practice or record your audition</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-kod-purple-600">
                <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Song</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Genre</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-white/50 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kod-purple-600/50">
              {songs.map(song => (
                <tr key={song.id} className="hover:bg-kod-purple-700/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <img src={song.cover_url} alt={song.title} className="w-10 h-10 rounded-md object-cover mr-3" />
                      <div>
                        <div className="font-medium text-white">{song.title}</div>
                        <div className="text-xs text-white/60">{song.artist}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white/70">{song.genre}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button 
                        onClick={(e) => recordSong(e, song)}
                        className="px-3 py-1.5 bg-kod-accent hover:bg-kod-accent-dark text-white rounded-md text-xs font-medium transition-colors"
                      >
                        Sing Now!
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};